import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import * as StoryActions from './story.actions';
import {initialize, loadEvents, state, timelineUpdate} from './story.actions';
import * as CampaignSelectors from '../campaigns/campaign.selectors';
import * as StorySelectors from './story.selectors';
import {selectActiveStoryCharacters, selectStoryCharacterCandidates} from './story.selectors';
import {ActionService, CharactersService, EventsService} from 'gamesis-story-client';
import {EMPTY, forkJoin, of} from 'rxjs';
import {exitCampaign, setCampaign} from '../campaigns/campaign.actions';
import {NGXLogger} from 'ngx-logger';
import {isGameMaster} from '../players/player.selectors';
import {deselectCharacter, selectCharacter, setMode} from '../settings/settings.actions';
import {selectMode} from '../settings/settings.selectors';
import {Mode} from '../settings/settings.model';
import {selectCurrentCharacter} from '../characters/character.selectors';

@Injectable()
export class StoryEffects {
    loadEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.loadEvents, setCampaign),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId)),
                this.store.pipe(select(StorySelectors.selectLoaded)),
                this.store.pipe(select(StorySelectors.selectCount)),
            ),
            concatMap(([_, campaign, loaded, count]) => {
                if (campaign) {
                    if (count === undefined || loaded < count) {
                        const page = Math.floor(loaded / 10);
                        return this.service.getTimeline(campaign, page).pipe(
                            map(paged => StoryActions.loadEventsSuccess({
                                count: paged.count,
                                events: paged.data
                            })),
                            catchError(error => of(StoryActions.eventFailure({error})))
                        );
                    } else {
                        return of(StoryActions.loadEventsSuccess({count: count}));
                    }

                } else {
                    return of(StoryActions.eventFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    handleLoadEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.loadEventsSuccess),
            withLatestFrom(
                this.store.select(StorySelectors.selectStory),
                this.store.select(StorySelectors.selectHasMore)
            ),
            mergeMap(([_, story, hasMore]) => {
                if (!story && hasMore) {
                    return of(StoryActions.loadEvents());
                } else {
                    return EMPTY;
                }
            })
        )
    );

    deleteEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.deleteEvent),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.service.deleteEvent(campaign, action.id)
                .pipe(
                    map(() => StoryActions.deleteEventSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    updateEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.updateEvent),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.service.updateEvent(campaign, action.id, action.event)
                .pipe(
                    map(() => StoryActions.updateEventSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    deleteActionItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.deleteActionItem),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.actionService.deleteActionItem(campaign, action.event, action.item)
                .pipe(
                    map(() => StoryActions.updateEventSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    deleteImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.deleteImage),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.service.deleteImage(campaign, action.id)
                .pipe(
                    map(() => StoryActions.updateEventSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    addImages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.addImages),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) =>
                forkJoin(
                    action.files
                        .map(file => this.service.addImage(campaign, action.story, file)
                            .pipe(
                                catchError(error => of({file, error}))
                            ))
                ).pipe(
                    map(() => StoryActions.updateEventSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    react$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.react),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.service.react(campaign, action.id, action.text)
                .pipe(
                    map(() => StoryActions.reactSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    setActiveCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.loadEventsSuccess, timelineUpdate),
            withLatestFrom(
                this.store.pipe(select(selectMode)),
                this.store.pipe(select(selectActiveStoryCharacters)),
                this.store.pipe(select(selectCurrentCharacter)),
                this.store.pipe(select(selectStoryCharacterCandidates)),
                this.store.pipe(select(isGameMaster))
            ),
            filter(([_, mode]) => mode !== Mode.story),
            mergeMap(([action, _, playing, current, candidates, gm]) => {
                // no characters active in this story, deselect
                if (playing.length === 0 || (current === null && candidates.length === 0)) {
                    return of(setMode({mode: gm ? Mode.story : Mode.comment}));
                }

                // if the current playing character is not active in this round, select first candidate
                if (!playing.some(character => character.id === current?.id)) {
                    if (candidates.length > 0) {
                        return of(selectCharacter(candidates[0]));
                    } else {
                        return of(setMode({mode: gm ? Mode.story : Mode.comment}));
                    }
                }

                return EMPTY;
            })
        )
    );

    reset$ = createEffect(() =>
        this.actions$.pipe(
            ofType(exitCampaign),
            switchMap(_ => of(initialize()))
        )
    );

    initialize = createEffect(() =>
        this.actions$.pipe(
            ofType(initialize),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            switchMap(([_, campaign]) => campaign ? of(loadEvents()) : EMPTY)
        )
    );

    synchronize$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.synchronize),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))
            ),
            switchMap(([_, campaign]) => {
                if (campaign) {
                    return this.service.getTimeline(campaign).pipe(
                        map(paged => StoryActions.synchronizeSuccess({
                            events: paged.data,
                            count: paged.total
                        })),
                        catchError(error => of(StoryActions.eventFailure({error})))
                    );
                } else {
                    return of(StoryActions.eventFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    synchronizeDone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.synchronizeSuccess),
            withLatestFrom(
                this.store.pipe(select(StorySelectors.selectEvents))
            ),
            switchMap(([action, events]) => {
                const unsaved = action.events.filter(event => !events.find(e => e.id === event.id));

                if (unsaved.length === 0) { // all events were save
                    return EMPTY;
                } else if (unsaved.length < action.events.length) {
                    return of(StoryActions.loadEventsSuccess({
                        events: action.events,
                        count: action.count
                    }));
                } else {
                    return of(StoryActions.initialize());
                }
            })
        )
    );

    round$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.round),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.service.round(campaign, action.round)
                .pipe(
                    map(() => StoryActions.createEventSuccess()),
                    catchError(error => of(StoryActions.eventFailure({error})))
                ))
        )
    );

    speak$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.speak),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId)),
                this.store.pipe(select(selectCurrentCharacter))
            ),
            mergeMap(([action, campaign, character]) =>
                (action.id ?
                        this.service.speakUpdate(campaign, character.id, action.id, action.speech) :
                        this.service.speak(campaign, character.id, action.speech)
                )
                    .pipe(
                        map(() => StoryActions.createEventSuccess()),
                        catchError(error => of(StoryActions.eventFailure({error})))
                    ))
        )
    );

    act$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.act),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId)),
                this.store.pipe(select(selectCurrentCharacter))
            ),
            mergeMap(([action, campaign, character]) =>
                (
                    action.id ?
                        this.service.actUpdate(campaign, character.id, action.id, action.action) :
                        this.service.act(campaign, character.id, action.action)
                )
                    .pipe(
                        map(() => StoryActions.createEventSuccess()),
                        catchError(error => of(StoryActions.eventFailure({error})))
                    ))
        )
    );

    comment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.comment),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) =>
                (action.id ?
                        this.service.commentUpdate(campaign, action.id, action.comment) :
                        this.service.comment(campaign, action.comment)
                )
                    .pipe(
                        map(() => StoryActions.createEventSuccess()),
                        catchError(error => of(StoryActions.eventFailure({error})))
                    ))
        )
    );

    rollDice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.rollDice),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId)),
                this.store.pipe(select(selectCurrentCharacter))
            ),
            mergeMap(([action, campaign, character]) => this.service.roll(campaign, character.id, {
                type: action.rollType,
                formula: action.formula,
                text: action.text
            }).pipe(
                map(() => StoryActions.rollSuccess()),
                catchError(error => of(StoryActions.rollFailure()))
            ))
        )
    );

    rollStoredDice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StoryActions.rollStoredDice),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId)),
                this.store.pipe(select(selectCurrentCharacter))
            ),
            mergeMap(([action, campaign, character]) => this.service.rollIt(campaign, character.id, action.id, action.rollType)
                .pipe(
                    map(() => StoryActions.rollSuccess()),
                    catchError(error => of(StoryActions.rollFailure()))
                ))
        )
    );

    deselectCharacter = createEffect(() =>
        this.actions$.pipe(
            ofType(setMode),
            mergeMap((action) => {
                if (action.mode === Mode.story || action.mode === Mode.comment) {
                    return of(deselectCharacter());
                }
                return EMPTY;
            })
        )
    );

    setMessageMode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(selectCharacter),
            withLatestFrom(this.store.select(selectMode)),
            mergeMap(([_, mode]) => {
                if (mode === Mode.comment || mode === Mode.story) {
                    return of(setMode({mode: Mode.message}));
                }
                return EMPTY;
            })
        )
    );

    state$ = createEffect(() =>
        this.actions$.pipe(
            ofType(state),
            withLatestFrom(
                this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId)),
                this.store.pipe(select(selectCurrentCharacter))
            ),
            tap(([action, campaign, character]) => {
                if (campaign && character) {
                    this.charactersService.state(campaign, character.id, action.state).subscribe();
                }
            })
        ), {dispatch: false}
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private service: EventsService,
        private actionService: ActionService,
        private charactersService: CharactersService,
        private logger: NGXLogger
    ) {
    }
}
